import React, { useState } from "react";
import { useDebounce } from "use-debounce";
import {
  Table,
  Button,
  Form,
  Input,
  Tag,
  Popconfirm,
  message,
  Empty,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  SearchOutlined,
  PoweroffOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  useEmployeesQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
  useToggleEmployeeStatusMutation,
  useSectionAccess,
} from "../../Services";
import { Employee, HRAction } from "../../Types";
import { EmployeeForm } from "./common/EmployeeForm";
import { PAGE_SIZE, DEBOUNCED_SEARCH_QUERY } from "../../Constant";
import { PageHeader, tablePagination } from "../../components";
import { RecordStatus } from "../../Enums";
import dayjs from "dayjs";

export const EmployeesPage = () => {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search] = useDebounce(searchInput, DEBOUNCED_SEARCH_QUERY);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [form] = Form.useForm();

  // Queries
  const {
    data: employeeResponse,
    isLoading,
    refetch,
  } = useEmployeesQuery({
    page,
    limit: PAGE_SIZE,
    search,
  });

  const employees = employeeResponse?.data || [];
  const total = employeeResponse?.meta?.total || 0;

  // Mutations
  const createEmployeeMutation = useCreateEmployeeMutation();
  const updateEmployeeMutation = useUpdateEmployeeMutation();
  const toggleStatusMutation = useToggleEmployeeStatusMutation();

  const { isAllowed } = useSectionAccess("hr");

  const openDrawer = (employee?: Employee) => {
    if (employee) {
      setEditingEmployee(employee);
      form.setFieldsValue({
        first_name: employee.first_name,
        last_name: employee.last_name,
        nic: employee.nic || "",
        phone: employee.phone || "",
        address: employee.address || "",
        status: employee.status,
        designation: (employee as any).designation || "",
        hire_date: (employee as any).hire_date ? dayjs((employee as any).hire_date) : null,
      });
    } else {
      setEditingEmployee(null);
      form.resetFields();
      form.setFieldsValue({ status: RecordStatus.ACTIVE });
    }
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingEmployee(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const mutation = editingEmployee
        ? updateEmployeeMutation
        : createEmployeeMutation;

      const formattedValues = {
        ...values,
        hire_date: values.hire_date ? values.hire_date.format("YYYY-MM-DD") : null,
        designation: values.designation || null,
      };

      const payload = editingEmployee
        ? { id: editingEmployee.id, ...formattedValues }
        : formattedValues;

      const res = await mutation.mutateAsync(payload);

      if (res?.success) {
        message.success(
          editingEmployee
            ? "Employee updated successfully!"
            : "Employee created successfully!",
        );
        closeDrawer();
      } else {
        if (res?.errors) {
          Object.entries(res.errors).forEach(([field, msg]) => {
            form.setFields([{ name: field, errors: [msg as string] }]);
          });
        } else {
          message.error(res?.message || "Operation failed.");
        }
      }
    } catch (error) {
      message.error("Please fill all the required fields.");
    }
  };

  const handleToggleStatus = async (employee: Employee) => {
    const newStatus =
      employee.status === RecordStatus.ACTIVE
        ? RecordStatus.INACTIVE
        : RecordStatus.ACTIVE;

    try {
      const res = await toggleStatusMutation.mutateAsync({
        id: employee.id,
        status: newStatus,
      });

      if (res?.success) {
        message.success(`Employee marked as ${newStatus}.`);
      } else {
        message.error(res?.message || "Failed to change status.");
      }
    } catch (err: any) {
      message.error(err?.message || "Failed to change status.");
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    setPage(1);
  };

  const columns = [
    {
      title: "Employee",
      key: "name",
      render: (_: any, rec: Employee) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
            <span className="text-gray-600 font-semibold text-xs">
              {rec.first_name[0]?.toUpperCase()}
              {rec.last_name[0]?.toUpperCase()}
            </span>
          </div>
          <div>
            <p className="font-medium text-gray-800 text-sm m-0 leading-tight">
              {rec.first_name} {rec.last_name}
            </p>
            <div className="flex gap-2 items-center mt-1">
              {(rec as any).designation && (
                <span className="text-xs text-gray-500 font-medium bg-gray-100 border border-gray-200 px-1.5 py-0.5 rounded">
                  {(rec as any).designation}
                </span>
              )}
              {rec.linked_username && (
                <span className="text-xs text-gray-400">
                  @{rec.linked_username}
                </span>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "NIC",
      dataIndex: "nic",
      key: "nic",
      render: (v: string) =>
        v ? (
          <span className="text-sm text-gray-700">{v}</span>
        ) : (
          <span className="text-gray-300">—</span>
        ),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (v: string) =>
        v ? (
          <span className="text-sm text-gray-700">{v}</span>
        ) : (
          <span className="text-gray-300">—</span>
        ),
    },
    {
      title: "Linked User",
      dataIndex: "linked_username",
      key: "linked_username",
      render: (v: string) =>
        v ? (
          <Tag color="blue">@{v}</Tag>
        ) : (
          <span className="text-gray-300 text-xs">Not linked</span>
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s: string) => (
        <Tag color={s === "Active" ? "success" : "default"}>{s}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_: any, rec: Employee) => (
        <div className="flex items-center gap-1">
          {isAllowed(HRAction.ManageEmployees) && (
            <Tooltip title="Edit">
              <Button
                type="text"
                icon={<EditOutlined />}
                size="small"
                onClick={() => openDrawer(rec)}
              />
            </Tooltip>
          )}
          {isAllowed(HRAction.ManageEmployees) && (
            <Popconfirm
              title={`Mark as ${rec.status === "Active" ? "Inactive" : "Active"}?`}
              onConfirm={() => handleToggleStatus(rec)}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip
                title={rec.status === "Active" ? "Deactivate" : "Activate"}
              >
                <Button
                  type="text"
                  icon={<PoweroffOutlined />}
                  size="small"
                  danger={rec.status === "Active"}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <PageHeader
        title="Employee Management"
        subText={`Total: ${total} employees`}
        handleButtons={[
          ...(isAllowed(HRAction.ManageEmployees)
            ? [
                {
                  title: "Add Employee",
                  icon: PlusOutlined,
                  onClick: () => {
                    openDrawer(null);
                  },
                },
              ]
            : []),
          {
            title: "Refresh",
            icon: ReloadOutlined,
            onClick: refetch,
          },
        ]}
        searchBar={
          <Input
            placeholder="Search by name, NIC, phone…"
            prefix={<SearchOutlined />}
            value={searchInput}
            onChange={handleSearchChange}
            allowClear
            onClear={() => {
              setSearchInput("");
              setPage(1);
            }}
            style={{ width: 260 }}
          />
        }
      />

      <div className="mt-4 bg-white rounded-lg border border-gray-200 overflow-hidden">
        <Table
          dataSource={employees}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={tablePagination({
            current: page,
            pageSize: PAGE_SIZE,
            total,
            onChange: setPage,
            itemName: "employees",
          })}
          scroll={{ x: 800 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No employees found"
              />
            ),
          }}
        />
      </div>

      <EmployeeForm
        open={drawerOpen}
        onClose={closeDrawer}
        loading={
          createEmployeeMutation.isPending || updateEmployeeMutation.isPending
        }
        onSubmit={handleSubmit}
        editingEmployee={editingEmployee}
        form={form}
      />
    </div>
  );
};
